import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNav } from '../../store/nav';
import { useProfile } from '../../store/profile';
import { useToast } from '../../store/toast';
import { useMe } from '../../hooks/useMe';
import { setPinApi, removePinApi } from '../../lib/me';
import { ApiError } from '../../lib/api';
import { haptic } from '../../hooks/useTelegram';
import { FigIcon } from '../FigIcon';
import { Numpad, PinDots } from './Numpad';

const PIN_LEN = 4;
const GREEN = 'rgb(var(--positive))';
const RED = 'rgb(var(--negative))';
const BLUE = 'rgb(var(--accent))';

/** PIN creation flow — Figma section 2:7741. Set passcode → confirm → success
 *  (green) or mismatch (red "Wrong code") which resets back to set. */
export function PinScreen() {
  const { t } = useTranslation();
  const pop = useNav((s) => s.pop);
  const setPinSet = useProfile((s) => s.setPinSet);
  const localPin = useProfile((s) => s.pinSet);
  const localBio = useProfile((s) => s.biometricEnabled);
  const showToast = useToast((s) => s.show);
  const { me, token, refresh } = useMe();
  const pinSet = me?.pinSet ?? localPin;
  const biometricEnabled = me?.biometricEnabled ?? localBio;

  // Tapping the PIN row toggles: no PIN → set one; PIN on → enter it to remove.
  // Removing is blocked while biometric is enabled (biometric depends on the PIN).
  const mode: 'set' | 'remove' | 'blocked' = !pinSet
    ? 'set'
    : biometricEnabled
      ? 'blocked'
      : 'remove';

  const [step, setStep] = useState<'set' | 'confirm'>('set');
  const [status, setStatus] = useState<'idle' | 'busy' | 'success' | 'error'>('idle');
  const [first, setFirst] = useState('');
  const [entry, setEntry] = useState('');

  // One-shot transition timers live in a ref (not effect cleanup) so a re-render
  // from setStatus doesn't cancel them before they fire.
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const wrong = () => {
    setStatus('error');
    timer.current = setTimeout(() => {
      setStatus('idle');
      setStep('set');
      setFirst('');
      setEntry('');
    }, 950);
  };

  const complete = (code: string) => {
    // Remove mode: verify the entered PIN, then remove it.
    if (mode === 'remove') {
      const done = () => {
        setPinSet(false);
        setStatus('success');
        timer.current = setTimeout(pop, 750);
      };
      if (token) {
        setStatus('busy'); // disable the pad + show a spinner during the round-trip
        removePinApi(token, code)
          .then(() => {
            refresh();
            done();
          })
          .catch((e) => {
            if (e instanceof ApiError && e.code === 'biometric_enabled') {
              showToast(t('pinFlow.removeBlocked'));
              setStatus('idle');
              setEntry('');
              return;
            }
            wrong();
          });
      } else {
        done(); // dev fallback — no server to verify against
      }
      return;
    }

    // Set mode: enter → confirm.
    if (step === 'set') {
      setFirst(code);
      timer.current = setTimeout(() => {
        setEntry('');
        setStep('confirm');
      }, 150);
      return;
    }
    if (code === first) {
      setPinSet(true);
      if (token) setPinApi(token, code).then(refresh).catch(() => {});
      setStatus('success');
      timer.current = setTimeout(pop, 750);
    } else {
      wrong();
    }
  };

  // Functional update so rapid taps don't read a stale `entry` and overwrite
  // each other (previously a fast second tap saw the old value and dropped a
  // digit — the user had to pause ~1s between presses). Completion is detected
  // in an effect once the entry fills, keeping this reducer side-effect free.
  const press = (d: string) => {
    if (status !== 'idle') return;
    haptic();
    setEntry((prev) => (prev.length >= PIN_LEN ? prev : prev + d));
  };

  // Fire completion once the entry is full (set → confirm, or confirm check).
  useEffect(() => {
    if (status === 'idle' && entry.length === PIN_LEN) {
      haptic();
      complete(entry);
    }
    // complete closes over step/first, recreated each render — intentionally
    // keyed only on the entry/status change that should trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, status]);
  const backspace = () => {
    if (status !== 'idle') return;
    setEntry((e) => e.slice(0, -1));
  };

  const accent = status === 'success' ? GREEN : status === 'error' ? RED : null;
  const title =
    status === 'success'
      ? mode === 'remove'
        ? t('pinFlow.removed')
        : t('pinFlow.success')
      : status === 'error'
        ? t('pinFlow.error')
        : mode === 'blocked'
          ? t('pinFlow.removeBlocked')
          : mode === 'remove'
            ? t('pinFlow.removeTitle')
            : step === 'set'
              ? t('pinFlow.set')
              : t('pinFlow.confirm');

  return (
    <div className="push-enter relative flex min-h-screen flex-col bg-card">
      {/* whole block centered vertically + horizontally */}
      <div className="flex flex-1 flex-col items-center justify-center px-4">
        {/* lock + title + dots */}
        <div className="flex flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-8" style={{ color: accent ?? 'rgb(var(--ink))' }}>
            <FigIcon name="pfLock" size={24} />
            <p className="max-w-[280px] text-center text-[20px] font-medium leading-[1.3]">{title}</p>
          </div>
          {mode !== 'blocked' && (
            <PinDots len={PIN_LEN} count={entry.length} color={accent ?? BLUE} />
          )}
        </div>

        {/* keypad — hidden in the blocked state (can't remove while biometric on);
            replaced by a spinner while a PIN-removal round-trip is in flight. */}
        {mode !== 'blocked' && (
          <div className="mt-16 grid place-items-center" style={{ minHeight: 340 }}>
            {status === 'busy' ? (
              <span className="size-9 animate-spin rounded-full border-[3px] border-border border-t-accent" />
            ) : (
              <Numpad onPress={press} onBackspace={backspace} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
